// DriftEventsStrip.js - Compact recent-drift-events strip for Asset Details Redux V2+ (PRD-43)
// Shows the N most severe OPEN drift events with click-to-act.
// Field names match the real drift-events API: ruleSeverity, ruleName, createdAt, status.

import React, { useState, useEffect } from 'react';
import { getEvents } from '../../services/driftService';
import './DriftEventsStrip.css';

// Mirrors api/drift-events SEVERITY_ORDER — lower = more severe
const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

function timeAgo(iso) {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return iso;
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
}

function severityClass(sev) {
    const s = String(sev || 'info').toLowerCase();
    if (s === 'critical') return 'drift-strip-item--critical';
    if (s === 'high') return 'drift-strip-item--high';
    if (s === 'medium') return 'drift-strip-item--medium';
    if (s === 'low') return 'drift-strip-item--low';
    return 'drift-strip-item--info';
}

function getEventTitle(ev) {
    // ruleName is the canonical title; detail is a human-readable explanation
    return ev.ruleName || ev.detail || ev.driftSummary || 'Drift event';
}

function isOpenOrActionable(ev) {
    // 'open' and 'acknowledged' still warrant attention; hide snoozed/suppressed/resolved
    if (!ev.status) return true;
    return ev.status === 'open' || ev.status === 'acknowledged';
}

/**
 * @param {object} props
 * @param {string} props.assetId
 * @param {number} [props.limit] - default 5
 * @param {'severity'|'recent'} [props.sortBy] - default 'severity'
 * @param {function} [props.onEventClick] - handler called with the event object
 */
function DriftEventsStrip({ assetId, limit = 5, sortBy = 'severity', onEventClick }) {
    const [events, setEvents] = useState([]);
    const [totalActionable, setTotalActionable] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!assetId) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                // driftService.getEvents returns the array directly (unwrapped from { events })
                const raw = await getEvents(assetId);
                const list = Array.isArray(raw) ? raw : (raw?.events || []);
                if (cancelled) return;

                const actionable = list.filter(isOpenOrActionable);
                setTotalActionable(actionable.length);

                const sorted = actionable.slice().sort((a, b) => {
                    if (sortBy === 'severity') {
                        const aOrder = SEVERITY_ORDER[a.ruleSeverity] ?? 5;
                        const bOrder = SEVERITY_ORDER[b.ruleSeverity] ?? 5;
                        if (aOrder !== bOrder) return aOrder - bOrder;
                        // Tie-break: newer first
                        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                    }
                    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                });
                setEvents(sorted);
            } catch (err) {
                if (!cancelled) setError(err.message || 'Failed to load');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [assetId, sortBy]);

    if (loading) {
        return <div className="drift-strip drift-strip--loading">Loading drift events…</div>;
    }

    if (error) {
        return <div className="drift-strip drift-strip--empty">Could not load drift events.</div>;
    }

    if (!events.length) {
        return (
            <div className="drift-strip drift-strip--empty">
                <span className="drift-strip__empty-icon" aria-hidden="true">✓</span>
                <span>No open drift events</span>
            </div>
        );
    }

    const visible = events.slice(0, limit);
    const hidden = totalActionable - visible.length;

    return (
        <div className="drift-strip">
            <div className="drift-strip__header">
                <span className="drift-strip__title">
                    {sortBy === 'severity' ? 'Top Drift Events by Severity' : 'Recent Drift Events'}
                </span>
                <span className="drift-strip__count">{totalActionable} open</span>
            </div>
            <ul className="drift-strip__list">
                {visible.map((ev) => {
                    const title = getEventTitle(ev);
                    const sev = (ev.ruleSeverity || 'info').toLowerCase();
                    return (
                        <li
                            key={ev.eventId || `${ev.assetId}-${ev.createdAt}`}
                            className={`drift-strip-item ${severityClass(sev)}`}
                            onClick={() => onEventClick && onEventClick(ev)}
                            onKeyDown={(e) => {
                                if ((e.key === 'Enter' || e.key === ' ') && onEventClick) {
                                    e.preventDefault();
                                    onEventClick(ev);
                                }
                            }}
                            role={onEventClick ? 'button' : undefined}
                            tabIndex={onEventClick ? 0 : undefined}
                        >
                            <span className="drift-strip-item__sev">{sev}</span>
                            <span className="drift-strip-item__desc" title={title}>{title}</span>
                            <span className="drift-strip-item__time">{timeAgo(ev.createdAt)}</span>
                        </li>
                    );
                })}
            </ul>
            {hidden > 0 && (
                <div className="drift-strip__more">
                    +{hidden} more open · click any event to open the Drift tab
                </div>
            )}
        </div>
    );
}

export default DriftEventsStrip;
